/**
 * Inline charts for the chat panel.
 *
 * Each branch in the dispatcher receives a typed `ChartSpec` and reuses
 * the existing MARS portal chart builders so the chat visual matches
 * the corresponding portal tab.
 *
 * Chart height scales with the user-resizable panel; width fills the
 * container and PlotlyChart's `useResizeHandler` handles live resize.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Data, Layout } from 'plotly.js';

import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { mergeLayout } from '@/lib/plotly/defaults';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';

import {
  buildFanLayout,
  buildFanTraces,
  FAN_CHARTS,
  mergePreviousMonth,
  processVintageData,
} from '@/lib/plotly/fanChart';
import { ASSET_COLORS } from '@/lib/plotly/colors';

import {
  ASSET_COLORS_DECOMP,
  buildDecompositionLayout,
  buildDecompositionTraces,
  buildTermStructureLayout,
  buildTermStructureTraces,
} from '@/features/mars/forecasts/lib/decompositionCharts';
import { buildAssetVolTraces, type VolAsset } from '@/lib/plotly/assetClassVolChart';
import {
  buildExposuresTraces,
  computeExposuresYRange,
  sliceExposures,
  type ExposureAsset,
} from '@/lib/plotly/portfolioExposuresChart';
import {
  fetchDecomposition,
  fetchReturnsVintageSnapshot,
  fetchSvar,
  marsForecastsKeys,
  type VintageReturnsSnapshot,
} from '@/features/mars/forecasts/api';
import { buildSvarLayout, buildSvarTraces } from '@/features/mars/forecasts/lib/svarCharts';
import type { SvarVariable } from '@/features/mars/forecasts/types';

import {
  fetchExposures,
  fetchVintageMulti,
  fetchVolatility,
  mlKeys,
} from '@/features/mars/live/api';
import type { LiveFundId } from '@/features/mars/live/store';

import type { Asset, ChartSpec, Mandate } from './chartSpec';
import { usePortalChatStore } from './store';

export default function ChatChart({ spec }: { spec: ChartSpec }): JSX.Element {
  switch (spec.name) {
    case 'svar_wow':
    case 'svar_cumulative':
      return (
        <SvarChatChart
          variable={spec.variable}
          mode={spec.name === 'svar_wow' ? 'wow' : 'cumulative'}
          startDate={spec.start_date ?? null}
          endDate={spec.end_date ?? null}
        />
      );
    case 'macro_fan':
      return <MacroFanChart variable={spec.variable} vintage={spec.vintage ?? null} />;
    case 'term_structure_curve':
      return (
        <TermStructureChart
          mandate={spec.mandate}
          asset={spec.asset}
          oldDate={spec.old_date}
          newDate={spec.new_date}
          which="curve"
        />
      );
    case 'term_structure_decomp':
      return (
        <TermStructureChart
          mandate={spec.mandate}
          asset={spec.asset}
          oldDate={spec.old_date}
          newDate={spec.new_date}
          which="decomp"
        />
      );
    case 'expected_returns_curve':
      return (
        <ExpectedReturnsCurve
          asset={spec.asset}
          date={spec.date}
          returnType={spec.return_type ?? 'excess'}
        />
      );
    case 'asset_class_volatility':
      return (
        <VolatilityChart
          assets={spec.assets ?? null}
          startDate={spec.start_date ?? null}
          endDate={spec.end_date ?? null}
        />
      );
    case 'portfolio_exposures':
      return (
        <ExposuresChart
          fund={spec.fund}
          startDate={spec.start_date ?? null}
          endDate={spec.end_date ?? null}
        />
      );
    default:
      return <ChartFallback message={`Unknown chart: ${(spec as ChartSpec).name}`} />;
  }
}

// ─── Shared sizing ────────────────────────────────────────────

function useChartHeight(): number {
  const panelHeight = usePortalChatStore((s) => s.panelHeight);
  return useMemo(() => Math.max(200, Math.min(440, Math.round(panelHeight * 0.42))), [panelHeight]);
}

function ChartFrame({
  height,
  children,
}: {
  height: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className="my-2 rounded-md border border-gray-200 bg-white p-1.5"
      style={{ height: `${height}px` }}
    >
      {children}
    </div>
  );
}

function ChartFallback({
  message,
  height = 140,
}: {
  message: string;
  height?: number;
}): JSX.Element {
  return (
    <div
      className="my-2 flex items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-[0.78rem] italic text-gray-500"
      style={{ height: `${height}px` }}
    >
      {message}
    </div>
  );
}

// Shared typography for every inline chat chart. Keeps tick labels,
// axis titles, legend text and the chart title at a consistent scale
// so they read as one visual system (earlier code drifted: SVAR used
// 10/12, the rest used 9/10, with mismatched axis-title sizes).
const CHAT_FONT = {
  base: 10, // legend + general
  tick: 9, // x/y tick labels
  axisTitle: 10, // y-axis title (e.g. "% NAV")
  chartTitle: 13, // chart title
};

/** Tighten margins / fonts / legend for any Plotly layout going into the chat. */
function chatLayout(layout: Partial<Layout>): Partial<Layout> {
  // Preserve incoming xaxis/yaxis options but force fonts.
  const xaxis: Partial<Layout['xaxis']> = {
    ...(layout.xaxis ?? {}),
    tickfont: { ...(layout.xaxis?.tickfont ?? {}), size: CHAT_FONT.tick },
    title: layout.xaxis?.title
      ? {
          ...(typeof layout.xaxis.title === 'object'
            ? layout.xaxis.title
            : { text: String(layout.xaxis.title) }),
          font: { size: CHAT_FONT.axisTitle },
        }
      : undefined,
  };
  const yaxis: Partial<Layout['yaxis']> = {
    ...(layout.yaxis ?? {}),
    tickfont: { ...(layout.yaxis?.tickfont ?? {}), size: CHAT_FONT.tick },
    title: layout.yaxis?.title
      ? {
          ...(typeof layout.yaxis.title === 'object'
            ? layout.yaxis.title
            : { text: String(layout.yaxis.title) }),
          font: { size: CHAT_FONT.axisTitle },
        }
      : undefined,
  };
  const title = layout.title
    ? {
        ...(typeof layout.title === 'object' ? layout.title : { text: String(layout.title) }),
        font: {
          ...(typeof layout.title === 'object' ? layout.title.font : {}),
          size: CHAT_FONT.chartTitle,
        },
      }
    : undefined;
  return mergeLayout({
    ...layout,
    margin: { t: 32, r: 12, b: 38, l: 46 },
    title,
    legend: {
      ...(layout.legend ?? {}),
      orientation: 'h' as const,
      yanchor: 'top' as const,
      y: -0.22,
      x: 0.5,
      xanchor: 'center' as const,
      font: { ...(layout.legend?.font ?? {}), size: CHAT_FONT.base },
    },
    xaxis,
    yaxis,
    autosize: true,
  } as Partial<Layout>);
}

// ─── SVAR ─────────────────────────────────────────────────────

function SvarChatChart({
  variable,
  mode,
  startDate,
  endDate,
}: {
  variable: string;
  mode: 'cumulative' | 'wow';
  startDate: string | null;
  endDate: string | null;
}): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: marsForecastsKeys.svar(),
    queryFn: fetchSvar,
    staleTime: 5 * 60 * 1000,
  });
  const h = useChartHeight();
  const built = useMemo(() => {
    if (!data) return null;
    const v = data.variables.find((x) => x.key === variable);
    if (!v) return null;
    const sliced = sliceSvar(v, startDate, endDate);
    return { traces: buildSvarTraces(sliced, mode), layout: buildSvarLayout(sliced, mode) };
  }, [data, variable, mode, startDate, endDate]);

  if (isLoading) return <ChartFallback message="Loading chart…" height={h} />;
  if (isError) return <ChartFallback message="Failed to load chart data" height={h} />;
  if (!built) return <ChartFallback message={`No SVAR data for ${variable}`} height={h} />;
  return (
    <ChartFrame height={h}>
      {/* SVAR builders also flow through the unified chatLayout helper
          so font / legend / margin scaling stays consistent with the
          other chart types. */}
      <PlotlyChart data={built.traces} layout={chatLayout(built.layout)} />
    </ChartFrame>
  );
}

function sliceSvar(v: SvarVariable, start: string | null, end: string | null): SvarVariable {
  if (!start && !end) return v;
  const keep = v.dates.map((d) => (start ? d >= start : true) && (end ? d <= end : true));
  const dates = v.dates.filter((_, i) => keep[i]);
  const series: SvarVariable['series'] = {};
  for (const [k, arr] of Object.entries(v.series)) {
    if (!arr) continue;
    series[k as keyof typeof v.series] = arr.filter((_, i) => keep[i]);
  }
  return { ...v, dates, series };
}

// ─── Macro fan ────────────────────────────────────────────────

function MacroFanChart({
  variable,
  vintage,
}: {
  variable: string;
  vintage: string | null;
}): JSX.Element {
  // Accept the literal string 'latest' (or null) and resolve to the most
  // recent vintage by fetching the list. A bare YYYY-MM-DD goes through
  // unchanged.
  const needsLookup = !vintage || vintage === 'latest';
  const listQ = useQuery({
    queryKey: mlKeys.vintagesList(),
    queryFn: () => import('@/features/mars/live/api').then((m) => m.fetchVintageList()),
    enabled: needsLookup,
  });
  const resolvedVintage = needsLookup
    ? listQ.data
      ? listQ.data[listQ.data.length - 1]
      : null
    : vintage;
  const multiQ = useQuery({
    queryKey: mlKeys.vintageMulti(resolvedVintage ?? ''),
    queryFn: () => fetchVintageMulti(resolvedVintage ?? ''),
    enabled: Boolean(resolvedVintage),
  });
  const h = useChartHeight();
  const built = useMemo(() => {
    if (!multiQ.data) return null;
    const def = FAN_CHARTS.find((d) => d.variable === variable);
    if (!def) return null;
    const raw = multiQ.data[def.variable] ?? [];
    if (!raw.length) return null;
    const merged = mergePreviousMonth(raw, null);
    const processed = processVintageData(merged);
    return {
      traces: buildFanTraces(processed, def),
      layout: buildFanLayout(def, processed, null),
    };
  }, [multiQ.data, variable]);

  if (multiQ.isLoading || listQ.isLoading)
    return <ChartFallback message="Loading chart…" height={h} />;
  if (multiQ.isError) return <ChartFallback message="Failed to load fan data" height={h} />;
  if (!built) return <ChartFallback message={`No fan data for ${variable}`} height={h} />;
  return (
    <ChartFrame height={h}>
      <PlotlyChart data={built.traces} layout={chatLayout(built.layout)} />
    </ChartFrame>
  );
}

// ─── Term-structure (LHS curve and RHS decomp) ────────────────

const _MANDATE_LABEL: Record<Mandate, string> = {
  fqm: 'FQM',
  dar_nn: 'DAR NN',
  fidelity_balanced: 'Balanced',
  fidelity_conservative: 'Conservative',
};

function TermStructureChart({
  mandate,
  asset,
  oldDate,
  newDate,
  which,
}: {
  mandate: Mandate;
  asset: Asset;
  oldDate: string;
  newDate: string;
  which: 'curve' | 'decomp';
}): JSX.Element {
  // Decomposition endpoint accepts YYYYMMDD (no dashes).
  const compact = (d: string) => d.replace(/-/g, '');
  const dq = useQuery({
    queryKey: marsForecastsKeys.decomposition(mandate, asset, compact(oldDate), compact(newDate)),
    queryFn: () =>
      fetchDecomposition({
        mandate,
        asset,
        oldDate: compact(oldDate),
        newDate: compact(newDate),
      }),
    staleTime: 60_000,
  });
  const h = useChartHeight();
  const built = useMemo(() => {
    if (!dq.data) return null;
    if (which === 'curve') {
      const color = ASSET_COLORS_DECOMP[asset];
      return {
        traces: buildTermStructureTraces(dq.data, color),
        layout: buildTermStructureLayout(dq.data),
      };
    }
    return {
      traces: buildDecompositionTraces(dq.data),
      layout: buildDecompositionLayout(dq.data),
    };
  }, [dq.data, which, asset]);

  if (dq.isLoading) return <ChartFallback message="Loading chart…" height={h} />;
  if (dq.isError) {
    return (
      <ChartFallback
        height={h}
        message={`Decomposition unavailable for ${_MANDATE_LABEL[mandate]} ${asset} (${oldDate} → ${newDate})`}
      />
    );
  }
  if (!built) return <ChartFallback message="No decomposition data" height={h} />;
  return (
    <ChartFrame height={h}>
      <PlotlyChart data={built.traces} layout={chatLayout(built.layout)} />
    </ChartFrame>
  );
}

// ─── Expected-returns term-structure (one asset at one vintage) ─

const _ASSET_TO_ER_KEY: Record<Asset, string> = {
  equities: 'Equity',
  bonds: 'Bond10Y',
  commodities: 'Commodity',
  inflation: 'RealBond10Y',
};

const _ASSET_DISPLAY: Record<Asset, string> = {
  equities: 'Equities',
  bonds: 'Fixed Income',
  commodities: 'Commodities',
  inflation: 'Inflation Swaps',
};

function ExpectedReturnsCurve({
  asset,
  date,
  returnType,
}: {
  asset: Asset;
  date: string;
  returnType: 'excess' | 'nominal' | 'real';
}): JSX.Element {
  const q = useQuery({
    queryKey: ['mars', 'returns_vintage_snapshot', date, returnType],
    queryFn: () => fetchReturnsVintageSnapshot(date, returnType),
    staleTime: 60_000,
  });
  const h = useChartHeight();
  const built = useMemo(() => {
    if (!q.data) return null;
    const erKey = _ASSET_TO_ER_KEY[asset];
    const rows = pickReturnSeries(q.data, erKey, returnType);
    if (!rows.length) return null;
    const display = _ASSET_DISPLAY[asset];
    // ASSET_COLORS keyed by 'Equities'/'Bonds'/'Commodities'/'Inflation' (AssetClass).
    const colorKey =
      asset === 'equities'
        ? 'Equities'
        : asset === 'bonds'
          ? 'Bonds'
          : asset === 'commodities'
            ? 'Commodities'
            : 'Inflation';
    const color = ASSET_COLORS[colorKey];
    const x = rows.map((r) => r.horizon_months);
    const y = rows.map((r) => r.value);
    const traces: Data[] = [
      {
        type: 'scatter',
        mode: 'lines+markers',
        name: `${display} (${returnType})`,
        x,
        y,
        line: { color, width: 2.5 },
        marker: { color, size: 7 },
        hovertemplate: hoverTemplate({ name: display, suffix: '%', format: '.2f' }),
      },
    ];
    const layout = mergeLayout({
      ...chartLayout({
        family: 'timeseries',
        title: `${display} expected return term structure (${returnType})`,
      }),
      xaxis: { title: { text: 'Horizon (months)', standoff: 10 } },
      yaxis: { title: { text: '%', standoff: 10 }, ticksuffix: '%' },
    });
    return { traces, layout };
  }, [q.data, asset, returnType]);

  if (q.isLoading) return <ChartFallback message="Loading chart…" height={h} />;
  if (q.isError) return <ChartFallback message="Failed to load ER snapshot" height={h} />;
  if (!built) return <ChartFallback message={`No ER data for ${asset} on ${date}`} height={h} />;
  return (
    <ChartFrame height={h}>
      <PlotlyChart data={built.traces} layout={chatLayout(built.layout)} />
    </ChartFrame>
  );
}

function pickReturnSeries(
  snap: VintageReturnsSnapshot,
  erKey: string,
  rt: 'excess' | 'nominal' | 'real',
): Array<{ horizon_months: number; value: number }> {
  const entry = snap[erKey];
  if (!entry) return [];
  if (Array.isArray(entry)) return entry;
  return entry[rt] ?? [];
}

// ─── Asset-class volatility (time series) ─────────────────────

// The portfolio-timeseries endpoint returns vol series keyed by the
// capitalised asset name ('Equities', 'Bonds', 'Commodities',
// 'Inflation'); earlier code used lowercase and got no matches.
const _ASSET_VOL_KEY: Record<Asset, string> = {
  equities: 'Equities',
  bonds: 'Bonds',
  commodities: 'Commodities',
  inflation: 'Inflation',
};
const _ASSET_VOL_LABEL: Record<Asset, string> = {
  equities: 'Equities',
  bonds: 'Fixed Income',
  commodities: 'Commodities',
  inflation: 'Inflation Swaps',
};
const _ASSET_VOL_COLOR: Record<Asset, string> = {
  equities: '#ec665c',
  bonds: '#729dcd',
  commodities: '#ff9d4a',
  inflation: '#9467bd',
};

function VolatilityChart({
  assets,
  startDate,
  endDate,
}: {
  assets: Asset[] | null;
  startDate: string | null;
  endDate: string | null;
}): JSX.Element {
  // Market-level FQM vol surface via the login-only asset-class-vol
  // endpoint — works for every persona, not just funds the user can see.
  const q = useQuery({
    queryKey: mlKeys.volatility(),
    queryFn: fetchVolatility,
    staleTime: 5 * 60 * 1000,
  });
  const h = useChartHeight();
  const built = useMemo(() => {
    if (!q.data) return null;
    const selected: Asset[] =
      assets && assets.length ? assets : ['equities', 'bonds', 'commodities', 'inflation'];
    const volAssets: VolAsset[] = selected.map((a) => ({
      seriesKey: _ASSET_VOL_KEY[a],
      label: _ASSET_VOL_LABEL[a],
      color: _ASSET_VOL_COLOR[a],
    }));
    const traces = buildAssetVolTraces(q.data, { assets: volAssets, startDate, endDate });
    if (!traces.length) return null;
    const layout = mergeLayout({
      ...chartLayout({ family: 'timeseries', title: 'Asset class volatilities' }),
      xaxis: { type: 'date' as const },
      yaxis: { title: { text: '% Ann.', standoff: 10 }, ticksuffix: '%' },
    });
    return { traces, layout };
  }, [q.data, assets, startDate, endDate]);

  if (q.isLoading) return <ChartFallback message="Loading chart…" height={h} />;
  if (q.isError) return <ChartFallback message="Failed to load volatility data" height={h} />;
  if (!built) return <ChartFallback message="No volatility data in window" height={h} />;
  return (
    <ChartFrame height={h}>
      <PlotlyChart data={built.traces} layout={chatLayout(built.layout)} />
    </ChartFrame>
  );
}

// ─── Portfolio exposures (time series) ────────────────────────

const _MANDATE_TO_LIVE_FUND: Record<Mandate, LiveFundId> = {
  fqm: 'mars-fqm',
  dar_nn: 'mars-dar-nn',
  fidelity_balanced: 'mars-fidelity-balanced',
  fidelity_conservative: 'mars-fidelity-conservative',
};

function ExposuresChart({
  fund,
  startDate,
  endDate,
}: {
  fund: Mandate;
  startDate: string | null;
  endDate: string | null;
}): JSX.Element {
  const liveFund = _MANDATE_TO_LIVE_FUND[fund];
  const q = useQuery({
    // Base config (tc_on, sv_on, constraints_on).
    queryKey: mlKeys.exposures(liveFund, true, true, true),
    queryFn: () => fetchExposures(liveFund, true, true, true),
    staleTime: 5 * 60 * 1000,
  });
  const h = useChartHeight();
  const built = useMemo(() => {
    if (!q.data || !q.data.dates) return null;
    const ASSET_LIST: Asset[] = ['equities', 'bonds', 'commodities', 'inflation'];
    // Series keys in the exposures payload are capitalised display
    // names ('Equities', 'Bonds', …).
    const seriesKeyByAsset: Record<Asset, string> = {
      equities: 'Equities',
      bonds: 'Bonds',
      commodities: 'Commodities',
      inflation: 'Inflation',
    };
    const assetSpecs: ExposureAsset[] = ASSET_LIST.map((a) => ({
      key: seriesKeyByAsset[a],
      label: _ASSET_VOL_LABEL[a],
      color: _ASSET_VOL_COLOR[a],
    }));
    const keys = assetSpecs.map((a) => a.key);
    const sliced = sliceExposures(q.data, keys, startDate, endDate);
    if (!sliced.dates.length) return null;
    const traces = buildExposuresTraces(sliced, {
      assets: assetSpecs,
      strategic: q.data.steady_state_strategic ?? null,
    });
    if (!traces.length) return null;
    const [yMin, yMax] = computeExposuresYRange(sliced);
    const layout = mergeLayout({
      ...chartLayout({
        family: 'timeseries',
        title: `Portfolio exposures (${_MANDATE_LABEL[fund]})`,
      }),
      xaxis: { type: 'date' as const },
      yaxis: {
        title: { text: '% NAV', standoff: 10 },
        ticksuffix: '%',
        range: [yMin, yMax],
      },
    });
    return { traces, layout };
  }, [q.data, fund, startDate, endDate]);

  if (q.isLoading) return <ChartFallback message="Loading chart…" height={h} />;
  if (q.isError) return <ChartFallback message="Failed to load exposures" height={h} />;
  if (!built) return <ChartFallback message="No exposure data in window" height={h} />;
  return (
    <ChartFrame height={h}>
      <PlotlyChart data={built.traces} layout={chatLayout(built.layout)} />
    </ChartFrame>
  );
}
